

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary — Antique Gold
        default:
          'bg-gold text-surface hover:bg-gold-hover active:bg-gold-active',
        // Destructive — Oxblood
        destructive:
          'bg-oxblood text-cream hover:bg-oxblood-hover active:bg-oxblood-active',
        // Outline — warm border
        outline:
          'border-2 border-surface-4 bg-transparent text-cream hover:border-gold hover:text-gold',
        // Secondary — Surface background
        secondary:
          'bg-surface-2 text-cream hover:bg-surface-3',
        // Ghost — Minimal
        ghost:
          'text-warm-gray hover:bg-surface-2 hover:text-cream',
        // Link
        link:
          'text-gold underline-offset-4 hover:underline',
        // Success — Verdigris
        success:
          'bg-verdigris text-cream hover:bg-verdigris-hover active:bg-verdigris-active',
        // Rarity variants
        prime:
          'bg-gold text-surface hover:bg-gold-hover shadow-lg shadow-gold/20 active:bg-gold-active',
        legendary:
          'bg-gold text-surface hover:bg-gold-hover shadow-lg shadow-gold/20',
        elite:
          'bg-violet text-cream hover:bg-violet-hover shadow-lg shadow-violet/20',
        rare:
          'bg-verdigris text-cream hover:bg-verdigris-hover shadow-lg shadow-verdigris/20',
        // Currency variants
        //
        // `coins` is the primary CTA on every gameplay surface (Enter, Save
        // Picks, Claim Prize, Save Lineup — 19 call sites). It used to be a
        // left-to-right linear-gradient, i.e. precisely the "gradient buttons
        // that feel like mobile game CTAs" trope docs/DESIGN-SYSTEM.md tells us
        // to avoid. It is now struck metal: a flat gold face with a 1px inner
        // highlight along the top edge and a 1px inner shade along the bottom,
        // which reads as stamped brass rather than as a glow. Geometry, type
        // and interaction states are untouched — only the surface changed.
        coins:
          'bg-gold text-surface shadow-struck',
        gems:
          'bg-gradient-to-r from-verdigris to-forest text-cream',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-xl px-8 text-base',
        xl: 'h-14 rounded-xl px-10 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading...</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
